import React, { useState, useEffect } from "react";

const IS_DEV = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

const D7_COL = "#3b82f6";
const D3_COL = "#f97316";

function scoreColor(v) {
  if (v > 0.15)  return "#16a34a";
  if (v < -0.15) return "#dc2626";
  return "#d97706";
}

function effectColor(e) {
  if (e === "bullish") return "#16a34a";
  if (e === "bearish") return "#dc2626";
  return "#6b7280";
}

// ── Article contributors table ────────────────────────────────────────────────

function ContributorsTable({ rows, label }) {
  if (!rows || rows.length === 0)
    return <p style={{ color: "#9ca3af", fontSize: 13 }}>No articles found in window.</p>;
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", letterSpacing: "0.06em", marginBottom: 8 }}>
        {label}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <th style={{ textAlign: "left",  padding: "4px 8px", color: "#6b7280", fontWeight: 500 }}>Headline</th>
            <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 500, whiteSpace: "nowrap" }}>Score</th>
            <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 500, whiteSpace: "nowrap" }}>Weight</th>
            <th style={{ textAlign: "left",  padding: "4px 8px", color: "#6b7280", fontWeight: 500 }}>Rule</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "5px 8px", color: "#111827", lineHeight: 1.4, textAlign: "left" }}>{r.title}</td>
              <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600, color: scoreColor(r.adjusted), whiteSpace: "nowrap" }}>
                {r.adjusted >= 0 ? "+" : ""}{r.adjusted.toFixed(3)}
              </td>
              <td style={{ padding: "5px 8px", textAlign: "right", color: "#6b7280", whiteSpace: "nowrap" }}>
                {r.weight.toFixed(3)}
              </td>
              <td style={{ padding: "5px 8px", color: "#6b7280", fontSize: 11, textAlign: "left" }}>{r.rule || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Article scoring section ───────────────────────────────────────────────────

function ArticleScoring({ symbol, date }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState("d7");

  const run = () => {
    if (!symbol || !date) return;
    setLoading(true); setError(null); setData(null);
    fetch(`/dev/score/${symbol}/${date}`)
      .then(r => r.json())
      .then(json => {
        if (json.error) throw new Error(json.error);
        setData(json); setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button onClick={run} disabled={loading} style={{
          padding: "6px 18px", borderRadius: 4, border: "none",
          background: loading ? "#d1d5db" : "#111827",
          color: loading ? "#6b7280" : "#fff",
          fontSize: 12, fontWeight: 600, cursor: loading ? "default" : "pointer",
        }}>
          {loading ? "Scoring… wait up to 30s for first run" : `Score ${symbol} for ${date}`}
        </button>
        {data && (
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            D7: <b style={{ color: scoreColor(data.score_d7) }}>{data.score_d7 >= 0 ? "+" : ""}{data.score_d7.toFixed(3)} {data.label_d7}</b>
            &nbsp;|&nbsp;
            D3: <b style={{ color: scoreColor(data.score_d3) }}>{data.score_d3 >= 0 ? "+" : ""}{data.score_d3.toFixed(3)} {data.label_d3}</b>
            &nbsp;|&nbsp;{data.n_d7} articles (D7), {data.n_d3} (D3)
          </span>
        )}
      </div>

      {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}

      {data && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["d7", "Trend (D7)", D7_COL], ["d3", "Momentum (D3)", D3_COL]].map(([key, label, col]) => (
              <button key={key} onClick={() => setTab(key)} style={{
                padding: "4px 14px", borderRadius: 4, border: `1px solid ${tab === key ? col : "#e5e7eb"}`,
                background: tab === key ? col : "#f9fafb",
                color: tab === key ? "#fff" : "#374151",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>{label}</button>
            ))}
          </div>
          <ContributorsTable
            rows={tab === "d7" ? data.contributors_d7 : data.contributors_d3}
            label={tab === "d7"
              ? `Trend contributors — ${data.contributors_d7?.length ?? 0} articles in D7 window`
              : `Momentum contributors — ${data.contributors_d3?.length ?? 0} articles in D3 window`}
          />
        </>
      )}

      {!loading && !error && !data && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>
          Click the button to run FinBERT scoring on the cached news for this date.
        </p>
      )}
    </div>
  );
}

// ── Polarity rules section ────────────────────────────────────────────────────

function PolarityRules({ symbol }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    fetch(`/dev/rules/${symbol}`)
      .then(r => r.json())
      .then(json => { if (json.error) throw new Error(json.error); setData(json); })
      .catch(e => setError(e.message));
  }, [symbol]);

  if (error) return <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>;
  if (!data)  return <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</p>;

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 12, color: "#6b7280" }}>
        <b>Category:</b> {data.category} &nbsp;|&nbsp;
        <b>Keywords:</b> {data.keywords.length ? data.keywords.join(", ") : <em>none — all category articles used</em>}
      </div>

      {data.polarity_rules.length === 0 && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>No polarity rules defined. FinBERT score is used as-is.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.polarity_rules.map((r, i) => (
          <div key={i} style={{
            border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px",
            borderLeft: `3px solid ${effectColor(r.effect)}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.04em" }}>
                {r.name}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: effectColor(r.effect) + "18", color: effectColor(r.effect),
                textTransform: "uppercase",
              }}>{r.effect}</span>
              <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: "auto" }}>priority {r.priority}</span>
            </div>
            {r.description && (
              <p style={{ margin: "0 0 6px", fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{r.description}</p>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(r.topics || []).map((t, ti) => (
                <span key={ti} style={{
                  fontSize: 11, padding: "2px 8px", borderRadius: 4,
                  background: "#f3f4f6", color: "#374151",
                }}>{t}</span>
              ))}
            </div>
            {r.min_score != null && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#9ca3af" }}>min score: {r.min_score}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main DevPanel ─────────────────────────────────────────────────────────────

export default function DevPanel({ symbol, date }) {
  const [open, setOpen]       = useState(true);
  const [section, setSection] = useState("articles");

  if (!IS_DEV) return null;

  return (
    <div style={{ marginTop: 32, border: "2px dashed #f59e0b", borderRadius: 8, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", padding: "10px 16px", background: "#fffbeb",
          border: "none", cursor: "pointer", textAlign: "left",
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 13, fontWeight: 600, color: "#92400e", letterSpacing: "0.04em",
        }}
      >
        <span style={{ fontSize: 16 }}>⚙</span>
        DEV — Signal Investigation ({symbol} / {date})
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 400 }}>{open ? "collapse" : "expand"}</span>
      </button>

      {open && (
        <div style={{ padding: 16, background: "#fff" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
            {[["articles", "Article Scoring"], ["rules", "Polarity Rules"]].map(([key, label]) => (
              <button key={key} onClick={() => setSection(key)} style={{
                padding: "5px 16px", borderRadius: 4, border: "1px solid #e5e7eb",
                background: section === key ? "#111827" : "#f9fafb",
                color: section === key ? "#fff" : "#374151",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>{label}</button>
            ))}
          </div>

          {section === "articles" && <ArticleScoring symbol={symbol} date={date} />}
          {section === "rules"    && <PolarityRules symbol={symbol} />}
        </div>
      )}
    </div>
  );
}
